/**
 * MobileBottomNav — PWA-style thumb-accessible bottom nav (only < lg).
 * Shows 5 primary destinations. Secondary items live in the header
 * "hamburger" Sheet.
 *
 * Follows the Feb 2026 mobile-first PWA spec:
 *   • Fixed bottom, safe-area padded for iOS home-bar
 *   • 44x44 minimum tap targets
 *   • Active state uses primary color pill; inactive is muted-foreground
 */
import { NavLink } from "react-router-dom";
import {
    LayoutDashboard, Terminal as TerminalIcon, LineChart, Trophy, MessagesSquare,
    Building2, ShieldCheck, Lock,
} from "lucide-react";
import { useOnboardingGate, GATE_ALLOWED_PATHS } from "@/context/OnboardingGateContext";

// Contextual primary destinations by role. Every role gets 5 shortcuts.
function pickNavForRole(user) {
    const isBroker = user?.role === "broker";
    const isAdmin  = user?.role === "admin";
    if (isAdmin) {
        return [
            { to: "/admin",              label: "Admin",       icon: ShieldCheck,     end: true, testId: "mnav-admin" },
            { to: "/app/terminal",       label: "Terminal",    icon: TerminalIcon,               testId: "mnav-terminal" },
            { to: "/app/options",        label: "Options",     icon: LineChart,                  testId: "mnav-options" },
            { to: "/app/leaderboard",    label: "Leaders",     icon: Trophy,                     testId: "mnav-leaderboard" },
            { to: "/app/community",      label: "Community",   icon: MessagesSquare,             testId: "mnav-community" },
        ];
    }
    if (isBroker) {
        return [
            { to: "/agency",             label: "Agency",      icon: Building2,       end: true, testId: "mnav-agency" },
            { to: "/app",                label: "Dashboard",   icon: LayoutDashboard, end: true, testId: "mnav-dash" },
            { to: "/app/terminal",       label: "Terminal",    icon: TerminalIcon,               testId: "mnav-terminal" },
            { to: "/app/leaderboard",    label: "Leaders",     icon: Trophy,                     testId: "mnav-leaderboard" },
            { to: "/app/community",      label: "Community",   icon: MessagesSquare,             testId: "mnav-community" },
        ];
    }
    return [
        { to: "/app",                    label: "Dashboard",   icon: LayoutDashboard, end: true, testId: "mnav-dash" },
        { to: "/app/terminal",           label: "Terminal",    icon: TerminalIcon,               testId: "mnav-terminal" },
        { to: "/app/options",            label: "Options",     icon: LineChart,                  testId: "mnav-options" },
        { to: "/app/leaderboard",        label: "Leaders",     icon: Trophy,                     testId: "mnav-leaderboard" },
        { to: "/app/community",          label: "Community",   icon: MessagesSquare,             testId: "mnav-community" },
    ];
}

export default function MobileBottomNav({ user }) {
    const links = pickNavForRole(user);
    const { stage } = useOnboardingGate();
    const allowed = GATE_ALLOWED_PATHS[stage] || [];
    return (
        <nav
            data-testid="mobile-bottom-nav"
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
        >
            <div className="grid grid-cols-5">
                {links.map((n) => {
                    const Icon = n.icon;
                    const locked = stage !== "complete" && !allowed.includes(n.to);
                    if (locked) {
                        return (
                            <div
                                key={n.to}
                                aria-disabled="true"
                                data-testid={`${n.testId}-locked`}
                                className="flex flex-col items-center justify-center gap-0.5 min-h-[56px] py-1.5 text-muted-foreground/40 cursor-not-allowed"
                            >
                                <div className="flex items-center justify-center w-11 h-6 rounded-full relative">
                                    <Icon className="w-5 h-5" />
                                    <Lock className="w-2.5 h-2.5 absolute -top-0.5 -right-0.5" strokeWidth={2.5} />
                                </div>
                                <span className="text-[10px] font-medium leading-none">{n.label}</span>
                            </div>
                        );
                    }
                    return (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={n.testId}
                            className={({ isActive }) =>
                                "flex flex-col items-center justify-center gap-0.5 min-h-[56px] py-1.5 " +
                                (isActive ? "text-primary" : "text-muted-foreground hover:text-foreground")
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <div className={
                                        "flex items-center justify-center w-11 h-6 rounded-full transition-colors " +
                                        (isActive ? "bg-primary/10" : "")
                                    }>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <span className="text-[10px] font-medium leading-none">{n.label}</span>
                                </>
                            )}
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}
